/**
 * Debounced Gmail / Google-Contacts people search backing the freeform
 * new-contact picker.
 *
 * The body is extracted verbatim from `ContactEditModal.tsx` and parameterized
 * on a typed deps object. The orchestrator keeps the `useCallback` shell (with
 * its byte-identical `[]` dependency array) and delegates here, so reference
 * identity and hook order are unchanged (behavior-preserving — #3237).
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { emailApiBase } from '@/lib/email-api';

export interface GmailPersonResult {
  email: string;
  name: string;
  photoUrl: string | null;
  score: number;
}

export interface GoogleContact {
  name?: string;
  email?: string;
  phone?: string;
  organization?: string;
  title?: string;
  photoUrl?: string;
}

export interface SearchGmailPeopleDeps {
  setGmailResults: Dispatch<SetStateAction<GmailPersonResult[]>>;
  googleContactsRef: MutableRefObject<GoogleContact[]>;
}

/**
 * Query the contacts-search endpoint and merge any Google Contacts not already
 * present in the Gmail results. Updates `gmailResults` only when results are
 * non-empty so a refining query that returns nothing keeps the prior matches.
 */
export async function searchGmailPeople(query: string, deps: SearchGmailPeopleDeps): Promise<void> {
  const { setGmailResults, googleContactsRef } = deps;
  if (!query || query.length < 2) { setGmailResults([]); return; }
  try {
    const token = getAuthItem('xavior_auth_token');
    const userId = getAuthItem('userId');
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Workspace-Id': getActiveWorkspaceId() };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (userId) headers['X-Actor-Id'] = userId;
    const res = await fetch(`${emailApiBase()}/contacts-search?query=${encodeURIComponent(query)}&limit=5`, { headers });
    if (res.ok) {
      const data = await res.json();
      const gmailPeople = data.people || [];
      const gContacts: typeof googleContactsRef.current = data.googleContacts || [];
      googleContactsRef.current = gContacts;

      // Merge Google Contacts into results (add any not already in Gmail results)
      const seenEmails = new Set(gmailPeople.map((p: any) => p.email?.toLowerCase()).filter(Boolean));
      const seenNames = new Set(gmailPeople.map((p: any) => p.name?.toLowerCase()).filter(Boolean));
      for (const gc of gContacts) {
        const emailLower = gc.email?.toLowerCase();
        const nameLower = gc.name?.toLowerCase();
        if ((emailLower && seenEmails.has(emailLower)) || (nameLower && seenNames.has(nameLower))) continue;
        gmailPeople.push({ email: gc.email || '', name: gc.name || '', photoUrl: gc.photoUrl || null, score: 0 });
        if (emailLower) seenEmails.add(emailLower);
        if (nameLower) seenNames.add(nameLower);
      }
      // Only update if we got results; if empty, keep previous matches
      // (user may have added keywords like "hippo seeds" after a name match)
      if (gmailPeople.length > 0) {
        setGmailResults(gmailPeople);
      }
    }
  } catch { /* silent */ }
}
